import React from 'react';
import { useTranslation } from 'react-i18next';
import { getRoleLabel } from '@/i18n/labelMaps';

export interface RoleCounts {
  owner: number;
  admin: number;
  editor: number;
  contributor: number;
  viewer: number;
}

interface OverviewUsersByRoleCardProps {
  counts: RoleCounts;
  total: number;
  canViewDetails: boolean;
  onRoleClick: (role: string) => void;
}

const ROLES: Array<{ key: keyof RoleCounts; className: string }> = [
  { key: 'owner', className: 'owner' },
  { key: 'admin', className: 'admin' },
  { key: 'editor', className: 'editor' },
  { key: 'contributor', className: 'contributor' },
  { key: 'viewer', className: 'viewer' }
];

const OverviewUsersByRoleCard: React.FC<OverviewUsersByRoleCardProps> = ({
  counts,
  total,
  canViewDetails,
  onRoleClick
}) => {
  const { t } = useTranslation('overview');
  const maxCount = Math.max(...ROLES.map((r) => counts[r.key]), 1);

  return (
    <section className="overview-card overview-role-card" aria-labelledby="overview-users-by-role-title">
      <header className="overview-card__head">
        <h2 id="overview-users-by-role-title" className="overview-card__title">
          {t('usersByRole.title')}
          {total > 0 && <span className="overview-card__count">{total}</span>}
        </h2>
      </header>
      <div className="role-grid">
        {ROLES.map(({ key, className }) => {
          const count = counts[key];
          const label = getRoleLabel(key);
          const pct = total > 0 ? Math.round((count / maxCount) * 100) : 0;
          const clickable = canViewDetails && count > 0;

          return (
            <button
              key={key}
              type="button"
              className={`role-row role-row--${className}`}
              onClick={() => clickable && onRoleClick(key)}
              disabled={!clickable}
              title={clickable ? t('usersByRole.viewRoleUsers', { role: label }) : undefined}
            >
              <span className={`role-pill role-pill--${className}`}>{label}</span>
              <span className="role-row__bar">
                <span className="role-row__fill" style={{ width: `${pct}%` }} />
              </span>
              <span className="role-row__count">{count}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default OverviewUsersByRoleCard;
